/**
 * Page d'administration des crédits
 * Permet au Super Admin d'attribuer des crédits aux COOPERs et MEMBREs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Coins, Users, Gift, Search, Plus, History, CheckCircle, XCircle, RefreshCw, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function AdminCreditsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [attributionModal, setAttributionModal] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Formulaire d'attribution
  const [attributionForm, setAttributionForm] = useState({
    amount: '',
    reason: '',
    credit_type: 'bonus'
  });

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}` };

  // Charger la liste des utilisateurs avec leurs crédits
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      
      const response = await axios.get(`${API}/admin/credits/all?${params}`, { headers });
      setUsers(response.data.users || []);
    } catch {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  // Charger les dernières transactions
  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/credits/transactions?limit=30`, { headers });
      setTransactions(response.data.transactions || []);
    } catch {
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchTransactions();
  }, [fetchUsers, fetchTransactions]);

  // Filtrer les utilisateurs par recherche
  const filteredUsers = users.filter(u => {
    const search = searchTerm.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(search) ||
      (u.nom || '').toLowerCase().includes(search) ||
      (u.prenom || '').toLowerCase().includes(search)
    );
  });

  // Attribuer des crédits à un utilisateur
  const handleAttributeCredits = async () => {
    if (!attributionForm.amount || parseInt(attributionForm.amount) <= 0) {
      toast.error('Veuillez entrer un montant valide');
      return;
    }
    
    if (!attributionForm.reason.trim()) {
      toast.error('Veuillez indiquer la raison de l\'attribution');
      return;
    }

    setLoading(true);
    try {
      if (bulkMode && selectedUsers.length > 0) {
        // Attribution en masse
        const response = await axios.post(`${API}/admin/credits/attribute-bulk`, {
          user_ids: selectedUsers,
          amount: parseInt(attributionForm.amount),
          reason: attributionForm.reason,
          credit_type: attributionForm.credit_type
        }, { headers });
        
        toast.success(`${response.data.success_count} attribution(s) réussie(s)`);
        setSelectedUsers([]);
      } else if (selectedUser) {
        // Attribution individuelle
        const _response = await axios.post(`${API}/admin/credits/attribute`, {
          user_id: selectedUser.id,
          amount: parseInt(attributionForm.amount),
          reason: attributionForm.reason,
          credit_type: attributionForm.credit_type
        }, { headers });
        
        toast.success(`${attributionForm.amount} crédits attribués à ${selectedUser.email}`);
      }
      
      setAttributionModal(false);
      setSelectedUser(null);
      setBulkMode(false);
      setAttributionForm({ amount: '', reason: '', credit_type: 'bonus' });
      fetchUsers();
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution');
    } finally {
      setLoading(false);
    }
  };

  // Toggle sélection pour mode masse
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Sélectionner/désélectionner tous
  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  // Vérifier si l'utilisateur est Super Admin
  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">Cette page est réservée au Super Admin.</p>
            <Button className="mt-4" onClick={() => navigate(-1)}>Retour</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 text-white shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <BackButton variant="dark" label="Retour Dashboard" fallbackUrl="/superadmin/dashboard" />
            <Badge className="bg-amber-500 text-white">Super Admin</Badge>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Coins className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Gestion des Crédits</h1>
              <p className="text-purple-200">Attribuer des crédits aux COOPERs et MEMBREs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-green-100 text-sm">Total Utilisateurs</p>
                  <p className="text-3xl font-bold">{users.length}</p>
                </div>
                <Users className="h-8 w-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-blue-100 text-sm">COOPERs</p>
                  <p className="text-3xl font-bold">{users.filter(u => u.role?.toLowerCase() === 'cooper').length}</p>
                </div>
                <Gift className="h-8 w-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-purple-100 text-sm">MEMBREs</p>
                  <p className="text-3xl font-bold">{users.filter(u => ['member', 'membre'].includes(u.role?.toLowerCase())).length}</p>
                </div>
                <Users className="h-8 w-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-amber-100 text-sm">Crédits Distribués</p>
                  <p className="text-3xl font-bold">{users.reduce((sum, u) => sum + (u.total_credits || 0), 0)}</p>
                </div>
                <Coins className="h-8 w-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions et filtres */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Utilisateurs
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.length > 0 && (
                  <Button 
                    onClick={() => {
                      setBulkMode(true);
                      setAttributionModal(true);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Gift className="h-4 w-4 mr-2" />
                    Attribuer à {selectedUsers.length} sélectionné(s)
                  </Button>
                )}
                <Button variant="outline" onClick={fetchUsers} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filtres */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par email, nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filtrer par rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="cooper">COOPERs</SelectItem>
                  <SelectItem value="member">MEMBREs</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={toggleSelectAll}>
                {selectedUsers.length === filteredUsers.length ? 'Désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>

            {/* Liste des utilisateurs */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-10">
                      <input 
                        type="checkbox" 
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rôle</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Crédits Standard</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Crédits Bonus</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className={`hover:bg-gray-50 ${selectedUsers.includes(u.id) ? 'bg-purple-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input 
                          type="checkbox" 
                          checked={selectedUsers.includes(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{u.email}</p>
                          <p className="text-sm text-gray-500">{u.prenom} {u.nom}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={u.role?.toLowerCase() === 'cooper' ? 'default' : 'secondary'}>
                          {u.role?.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-mono">{u.credits_standard || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-mono text-purple-600">{u.credits_bonus || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="font-mono text-lg">
                          {u.total_credits || 0}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setBulkMode(false);
                            setAttributionModal(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Attribuer
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        Aucun utilisateur trouvé
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Historique des transactions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Dernières Attributions
            </CardTitle>
            <CardDescription>Historique des crédits attribués</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">Aucune transaction</p>
              ) : (
                transactions.map((t, idx) => (
                  <div key={t.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${t.type?.includes('admin') ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {t.type?.includes('admin') ? (
                          <Gift className="h-4 w-4 text-purple-600" />
                        ) : (
                          <Coins className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">
                          +{t.amount} crédits {t.credit_type === 'bonus' && <span className="text-purple-600">(bonus)</span>}
                        </p>
                        <p className="text-sm text-gray-500">{t.user_email}</p>
                        {t.reason && <p className="text-xs text-gray-400 italic">{t.reason}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR') : '-'}
                      </p>
                      {t.attributed_by_email && (
                        <p className="text-xs text-gray-400">par {t.attributed_by_email}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal d'attribution */}
      <Dialog open={attributionModal} onOpenChange={setAttributionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-purple-600" />
              {bulkMode 
                ? `Attribuer des crédits à ${selectedUsers.length} utilisateur(s)`
                : `Attribuer des crédits à ${selectedUser?.email}`
              }
            </DialogTitle>
            <DialogDescription>
              Les crédits bonus sont utilisés en priorité.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Nombre de crédits *</Label>
              <Input
                id="amount"
                type="number"
                min="1"
                max="10000"
                placeholder="Ex: 50"
                value={attributionForm.amount}
                onChange={(e) => setAttributionForm({...attributionForm, amount: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credit_type">Type de crédits</Label>
              <Select 
                value={attributionForm.credit_type} 
                onValueChange={(v) => setAttributionForm({...attributionForm, credit_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonus">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-purple-600" />
                      Crédits Bonus (prioritaires)
                    </div>
                  </SelectItem>
                  <SelectItem value="standard">
                    <div className="flex items-center gap-2">
                      <Coins className="h-4 w-4 text-blue-600" />
                      Crédits Standard
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">Raison de l'attribution *</Label>
              <Textarea
                id="reason"
                placeholder="Ex: Bonus de bienvenue, compensation, promotion..."
                value={attributionForm.reason}
                onChange={(e) => setAttributionForm({...attributionForm, reason: e.target.value})}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={handleAttributeCredits} 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirmer l'attribution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
